import { useMemo } from "react";
import { hasRoles } from "../../../core/Global";
import SideMenu, { ISideMenuItem } from "../../../components/layouts/aside/SideMenu";
import { FontAwesomeIcons } from "../../../core/Types";


export interface CRMMenuItem {
    id: string
    name: string
    icon: FontAwesomeIcons
    link: string
    roles: string[]
}

export interface CRMMenuGroup {
    key: string
    name: string
    icon: FontAwesomeIcons
    color: string
    items: CRMMenuItem[]
}

// Single source of truth for the CRM sidebar. Consumed by the desktop SideMenu
// and by the mobile Settings tab (outer group + per-category drawers).
export const CRM_MENU_GROUPS: CRMMenuGroup[] = [
    {
        key: "jobs", name: "Jobs", icon: "briefcase", color: "#007aff",
        items: [
            { id: "jobs-all", name: "All Jobs", icon: "briefcase", link: "/crm/jobs/all", roles: ["ArchivedJobs", "IsCustomer"] },
            { id: "jobs-closed", name: "Closed Jobs", icon: "briefcase-blank", link: "/crm/jobs/closed", roles: ["ArchivedJobs", "IsCustomer"] },
            { id: "jobs-canceled", name: "Canceled Jobs", icon: "ban", link: "/crm/jobs/canceled", roles: ["ArchivedJobs", "IsCustomer"] },
            { id: "jobs-invoices", name: "Job Invoices", icon: "file-invoice-dollar", link: "/crm/jobs/invoices", roles: ["ArchivedJobs", "IsCustomer"] },
        ]
    },
    {
        key: "rates", name: "Custom Rates", icon: "money-bill", color: "#34c759",
        items: [
            { id: "rates-techs", name: "Technician Rates", icon: "user-helmet-safety", link: "/crm/rates/techs", roles: ["ManageTechRates"] },
            { id: "rates-techsfee", name: "Technician Fees", icon: "money-bill", link: "/crm/rates/techfees", roles: ["ManageTechRates"] },
            { id: "rates-sources", name: "Source Rates", icon: "share-nodes", link: "/crm/rates/sources", roles: ["ManageSourceRates"] },
            { id: "rates-sourcefee", name: "Source Fees", icon: "money-bill-wave", link: "/crm/rates/sourcefee", roles: ["ManageSourceRates"] },
        ]
    },
    {
        key: "base", name: "Base Data", icon: "database", color: "#ff9500",
        items: [
            { id: "base-jobtypegroups", name: "Job Type Groups", icon: "layer-group", link: "/crm/base/jobtypegroups", roles: ["ManageJobTypeGroups"] },
            { id: "base-jobtypes", name: "Job Types", icon: "list", link: "/crm/base/jobtypes", roles: ["ManageJobTypes"] },
            { id: "base-jobstatuses", name: "Job Statuses", icon: "circle-dot", link: "/crm/base/jobstatuses", roles: ["ManageJobStatuses"] },
            { id: "base-jobsources", name: "Job Sources", icon: "share-nodes", link: "/crm/base/jobsources", roles: ["ManageJobSources"] },
            { id: "base-jobcolors", name: "Job Colors", icon: "palette", link: "/crm/base/jobcolors", roles: ["ManageJobColors", "IsCustomer", "IsSystemAdmin"] },
            { id: "base-cancelreasons", name: "Cancel Reasons", icon: "ban", link: "/crm/base/cancelreasons", roles: ["ManageCancelReasons", "IsCustomer", "IsSystemAdmin"] },
            { id: "base-metros", name: "Metros", icon: "location-dot", link: "/crm/base/metros", roles: ["ManageMetros"] },
            { id: "base-tags", name: "Tags", icon: "tag", link: "/crm/base/tags", roles: ["ManageTags"] },
            { id: "base-flags", name: "Flags", icon: "flag", link: "/crm/base/flags", roles: ["ManageFlags"] },
            { id: "base-taxes", name: "Taxes", icon: "percent", link: "/crm/base/taxes", roles: ["ManageTaxes", "IsCustomer"] },
        ]
    },
    {
        key: "settings", name: "CRM Settings", icon: "sliders", color: "#8e8e93",
        items: [
            { id: "settings-jobs-preferences", name: "Jobs Preferences", icon: "sliders", link: "/crm/settings/jobs-preferences", roles: ["SystemSettings"] },
            { id: "settings-send-job", name: "Send Job Settings", icon: "paper-plane", link: "/crm/settings/send-job", roles: ["SystemSettings"] },
            // Hidden until automation rules actually run; the route and page stay.
            // { id: "settings-automationrules", name: "Automation Rules", icon: "wand-magic-sparkles", link: "/crm/settings/automation-rules", roles: ["ManageAutomationRules", "IsCustomer", "IsSystemAdmin"] },
            { id: "settings-automessaging", name: "Auto Messaging Rules", icon: "clock-rotate-left", link: "/crm/settings/auto-messaging", roles: ["SystemSettings"] },
            { id: "settings-chats", name: "Chat Forward Rules", icon: "comment-lines", link: "/crm/settings/chats", roles: ["SystemSettings"] },
            { id: "settings-percall", name: "Percall Settings", icon: "arrow-right-arrow-left", link: "/crm/settings/percall", roles: ["SystemSettings", "IsCustomer", "IsSystemAdmin"] },
        ]
    },
]


export function useCRMSideMenuItems(): ISideMenuItem[] {
    return useMemo<ISideMenuItem[]>(() => {
        const result: ISideMenuItem[] = []
        for (const g of CRM_MENU_GROUPS) {
            const visible = g.items.filter(it => hasRoles(it.roles))
            if (visible.length === 0) continue
            result.push({ seperator: true, name: g.name })
            for (const it of visible) {
                result.push({ id: it.id, name: it.name, icon: it.icon, link: it.link })
            }
        }
        return result
    }, [])
}


export default function CRMSideMenu() {
    const items = useCRMSideMenuItems()
    return <SideMenu items={items} />
}
